import React from "react";
import { motion } from "framer-motion";
import { ASSETS } from "@/config";
import { useLanguage } from "@/i18n/LanguageContext";
import PageHeader from "@/components/PageHeader";

// Visual bar levels — states, not measurements (no fake numbers).
const LEVELS = [96, 88, 92, 84, 80];

export default function Agent() {
  const { t, lang } = useLanguage();
  const fields = t("agent.fields");
  const params = t("agent.params");

  return (
    <div data-testid="agent-page" className="relative">
      <PageHeader label={t("agent.label")} title={t("agent.title")} />

      <section className="mx-auto grid max-w-7xl gap-10 px-4 pb-24 pt-12 sm:px-6 lg:grid-cols-[420px_1fr]">
        {/* Character art */}
        <motion.div
          initial={{ opacity: 0, x: -28 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.65, ease: "easeOut" }}
          className="mizu-corners relative overflow-hidden border border-mizu-cyan/25 shadow-[0_0_50px_rgba(0,240,255,0.08)]"
        >
          <img
            src={ASSETS.art}
            alt="MIZUKARA — agent 001"
            data-testid="agent-art"
            className="aspect-[3/4] w-full animate-float-slow object-cover object-[30%_15%]"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#020611]/85 via-transparent to-[#020611]/30" />
          <div className="pointer-events-none absolute inset-x-0 h-16 animate-scan-y bg-gradient-to-b from-transparent via-mizu-cyan/10 to-transparent" />
          <div className="absolute inset-x-0 bottom-0 flex items-end justify-between p-4">
            <div>
              <div className="font-display text-lg font-bold tracking-[0.2em] text-white">
                MIZUKARA
              </div>
              <div className="font-jp text-[10px] tracking-[0.5em] text-mizu-cyan">
                {t("agent.jpName")}
              </div>
            </div>
            <div className="text-right font-mono text-[10px] leading-4 text-mizu-cyan/80">
              <div>ID // 001</div>
              <div className="flex items-center justify-end gap-1.5">
                <span className="h-1.5 w-1.5 animate-pulse-glow bg-mizu-cyan" />
                {t("common.agentOnline")}
              </div>
            </div>
          </div>
          <div className="absolute right-3 top-3 font-jp text-sm tracking-[0.4em] text-mizu-cyan/70 [writing-mode:vertical-rl]">
            自ら
          </div>
        </motion.div>

        {/* Data panel */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.65, ease: "easeOut", delay: 0.12 }}
          className="space-y-6"
        >
          <div className="mizu-panel mizu-corners p-6" data-testid="agent-fields">
            <div className="mb-4 border-b border-mizu-cyan/15 pb-2 font-mono text-[10px] tracking-[0.3em] text-mizu-cyan">
              {t("agent.label")}
            </div>
            <div className="grid gap-x-8 sm:grid-cols-2">
              {fields.map((f, i) => (
                <motion.div
                  key={f.key}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.25 + i * 0.06 }}
                  className="border-b border-mizu-cyan/8 py-3"
                >
                  <div className="font-mono text-[9px] tracking-[0.3em] text-mizu-faint">
                    {f.key}
                  </div>
                  <div
                    className={`mt-1 font-mono text-xs tracking-[0.12em] text-mizu-silver ${f.value === "ONLINE" || f.value === "オンライン" ? "text-mizu-cyan mizu-glow" : ""}`}
                    data-testid={`agent-field-${i}`}
                  >
                    {f.value}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

          <div className="mizu-panel mizu-corners p-6" data-testid="agent-params">
            <div className="mb-5 border-b border-mizu-cyan/15 pb-2 font-mono text-[10px] tracking-[0.3em] text-mizu-cyan">
              {t("agent.paramsTitle")}
            </div>
            <div className="space-y-5">
              {params.map((p, i) => (
                <div key={p.key} data-testid={`agent-param-${p.key.toLowerCase()}`}>
                  <div className="mb-1.5 flex items-center justify-between font-mono text-[10px] tracking-[0.25em]">
                    <span className="text-mizu-silver">{p.key}</span>
                    <span className="flex items-center gap-1.5 text-mizu-cyan">
                      <span className="h-1 w-1 animate-pulse-glow bg-mizu-cyan" />
                      {p.status}
                    </span>
                  </div>
                  <div className="h-1.5 w-full bg-mizu-elevated/60">
                    <motion.div
                      initial={{ width: 0 }}
                      whileInView={{ width: `${LEVELS[i % LEVELS.length]}%` }}
                      viewport={{ once: true }}
                      transition={{ duration: 1.1, delay: 0.2 + i * 0.12, ease: "easeOut" }}
                      className="h-full bg-gradient-to-r from-mizu-blue to-mizu-cyan shadow-[0_0_10px_rgba(0,240,255,0.4)]"
                    />
                  </div>
                </div>
              ))}
            </div>
            <p className="mt-5 font-mono text-[9px] text-mizu-faint">{t("agent.note")}</p>
          </div>
        </motion.div>
      </section>
    </div>
  );
}
